import React, { useContext, useState } from 'react';
import { TextField, Button, Grid, Typography, IconButton, LinearProgress, Alert, keyframes, Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion'; // Import framer-motion
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CancelIcon from '@mui/icons-material/Cancel';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { PickupTaskState } from '../../../../enums/tasks';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import TextFieldWithScan from '../../../common/textfield-with-scan/textfield-with-scan';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Success icon
import MultiPackageScanner, { validationResult } from '../../../common/multi-package-scanner/multi-package-scanner';
import { DeliveryState, Workflow } from '../../../../enums/orders';
import { displayLocation } from '../../../../utils/locations';
import { GenericTableProps } from '../../../common/generic-table/utils';
import { Decoding, DecodingWithOrderVal, generateUID, same, transformOrdersToDecodingWithOrderVal, transformPendingBarcodesToDecodingWithOrderVal } from '../../../../utils/encoding';
import { IconWithText } from '../../../common/styles/components';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';

interface ReceiveOrdersFormProps {
    task: IPickupTask;
}

// Define keyframes for the animation
const bounceAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;


function ReceiveOrdersForm({ task }: ReceiveOrdersFormProps) {
    const completePickupTask = async (orders: Decoding[], setIsLoading: (isLoading: boolean) => void, setResponse: (resp?: AxiosResponse) => void, setMessage: (m: string) => void, config: any) => {
        setIsLoading(true)
        setResponse(undefined)

        try {
            const requestData = {
                status: PickupTaskState.COMPLETED
            }

            const url = `${API_ENDPOINT}/api/v1/pickup-task/${task.id}/`
            const newResponse = await axios.patch(url, requestData, config)
            setResponse(newResponse)
            setMessage("Pickup task completed successfully.")
        } catch (e) {
            if (axios.isAxiosError(e)) {
                setResponse(e.response)
                console.log(e.response)
                setMessage(`failed to complete task: ${e.response?.statusText}`)
            } else {
                setMessage(`failed to complete task.`)
            }
        }

        setIsLoading(false)

    }

    const expected = [...transformOrdersToDecodingWithOrderVal(task.orders), ...transformPendingBarcodesToDecodingWithOrderVal(task.pending_barcodes ?? [])]
    console.log(expected)

    return <MultiPackageScanner
        label='package id'
        validator={(decodingWithVal: DecodingWithOrderVal): validationResult => {

            if (!expected.some(item => same(decodingWithVal, item))) {
                return {
                    validated: false,
                    error: "package does not belong to pickup task"
                }
            }

            if (decodingWithVal.order.delivery_state != DeliveryState.PICKED_UP)
                return {
                    validated: false,
                    error: "order is not picked up yet."
                }

            return { validated: true }
        }}
        callback={completePickupTask}
        allowUndoScan={true}
        actionType={'Receive At Warehouse'}
        expectedOrders={expected}
        getRenderOrdersProps={(orders: DecodingWithOrderVal[]): GenericTableProps<DecodingWithOrderVal> => {
            return {
                sourceView: "receive-orders-form",
                items: orders,
                fullWidth: true,
                columns: [
                    {
                        id: "order_id",
                        header: "Order ID",
                        display_body: (decoding) => decoding.type === "temporary" ? <IconWithText> <HourglassBottomIcon fontSize='small' />{decoding.id}</IconWithText> : <>{decoding.id}</>
                    },
                    {
                        id: "pickup_source",
                        header: task.workflow === Workflow.STANDARD ? "Merchant" : "Customer",
                        display_body: (order) => (
                            <>
                                {
                                    task.workflow === Workflow.STANDARD ? <>  {task.merchant?.name}
                                        <br />
                                        {displayLocation(task.merchant_location)}</> : <> {task.customer?.name}
                                        <br />
                                        {displayLocation(task.customer_location)} </>
                                }

                            </>
                        ),
                    },
                ],
                getItemUID: (decoding) => generateUID(decoding),
                selectedItems: new Map,
                isLoading: false,
            }

        }}

    />

}

export default ReceiveOrdersForm;